import React, { FunctionComponent, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface MenuItemProps {
  icon: FunctionComponent<SVGProps<SVGSVGElement>> | JSX.Element;
  endIcon?: JSX.Element;
  text: string;
  /** If provided, we'll treat this as a link (internal or external) */
  href?: string;
  /** If true and href is provided, open an external link instead of a React Router link */
  external?: boolean;
  /** If you need to attach onClick for either link or button */
  onClick?: () => void;
  className?: string;
  last?: boolean;
  orange?: boolean;
}

/**
 * MenuItem can be:
 * 1) External link: (external = true)
 * 2) Internal link: (external = false)
 * 3) Button:        (no href given)
 */
export const MenuItem: React.FC<MenuItemProps> = ({
  icon: Icon,
  endIcon,
  text,
  href,
  external,
  onClick,
  className,
  last,
  orange,
}) => {
  const { t } = useTranslation();

  // Common content for all variants
  const content = (
    <>
      <span className="flex gap-2">
        <Icon
          className={`${
            orange ? 'text-orange-btn' : 'text-gray'
          } h-[20px] w-[20px] md:h-[28px] md:w-[28px]`}
        />
        <span className={`${orange ? 'text-orange-btn' : ''}`}>{t(text)}</span>
      </span>

      {endIcon}
    </>
  );
  return (
    <li className={`${last ? '' : 'border-b'} border-border-menu ${className ?? ''}`}>
      {href && external && (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onClick}
          className="flex w-full items-center justify-between gap-2.5 py-4 font-semibold text-white md:gap-5 md:py-[1.625rem] md:text-[20px]"
        >
          {content}
        </a>
      )}
      {href && !external && (
        <Link
          to={href}
          onClick={onClick}
          className="flex w-full items-center justify-between gap-2.5 py-4 font-semibold text-white md:gap-5 md:py-[1.625rem] md:text-[20px]"
        >
          {content}
        </Link>
      )}
      {!href && (
        <button
          type="button"
          onClick={onClick}
          className="flex w-full items-center justify-between gap-2.5 py-4 font-semibold text-white md:gap-5 md:py-[1.625rem] md:text-[20px]"
        >
          {content}
        </button>
      )}
    </li>
  );
};
